import { useAlert } from '@hooks/useAlert';
import { FileUpload } from '@mui/icons-material';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import type { MIMEType } from '@/types/general';

type UploadButtonProps = {
    children?: React.ReactNode;
    onUpload: (files: FileList) => void;
    text?: string;
    accept?: MIMEType;
    buttonSize?: 'small' | 'medium' | 'large';
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const UploadButton = ({
    onUpload,
    children,
    text,
    accept,
    buttonSize,
    className,
    ...props
}: UploadButtonProps) => {
    const { setAlert } = useAlert();
    const [dragging, setDragging] = useState(false);

    const handleDragEvent = useCallback<React.DragEventHandler>((e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragging(e.type !== 'dragleave' && e.type !== 'dragexit');
    }, []);

    const validateFiles = (list: FileList) => {
        if (!accept) return true;

        for (const file of list) {
            if (file.type === accept) continue;
            setAlert(
                `Can only upload "${accept}" files!${file.type ? ` Attempted to upload "${file.type}"` : ''}`,
                'error'
            );
            return false;
        }

        return true;
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setDragging(false);

        if (!e.dataTransfer.files) return;
        if (!validateFiles(e.dataTransfer.files)) return;

        onUpload(e.dataTransfer.files);
    };

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!e.target.files) return;
        if (!validateFiles(e.target.files)) return;

        onUpload(e.target.files);

        // * We must clear the FileList for the DOM element,
        // ? Otherwise, files with the same name can't be uploaded twice
        // * ^^ the onChange event won't fire
        e.target.files = null;
        e.target.value = '';
    };

    return (
        <Box
            display='flex'
            gap='5px'
            alignItems='center'
            onDrop={handleDrop}
            onDragEnter={handleDragEvent}
            onDragOver={handleDragEvent}
            onDragLeave={handleDragEvent}
            onDragExit={handleDragEvent}>
            <Tooltip title={dragging ? 'Drop file here' : ''} open={true} placement='top'>
                <Button
                    className={className}
                    size={buttonSize}
                    component='label'
                    variant='contained'
                    startIcon={<FileUpload />}
                    color={dragging ? 'info' : 'primary'}>
                    {children ?? 'Upload'}
                    <input {...props} type='file' accept={accept} hidden onChange={handleUpload} />
                </Button>
            </Tooltip>
            {text && <Typography>{text}</Typography>}
        </Box>
    );
};
